import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Autocomplete,
  Avatar,
  Box,
  Card,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DeleteIcon from '@mui/icons-material/Delete';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  getActiveProviders,
  getSingleUser,
  updateClientCleanerRestrictions,
} from '@/features/users/shared/api';
import type {
  CleanerRestriction,
  CleanerRestrictionCleaner,
  ClientCleanerRestrictionDto,
  User,
} from '@/features/users/shared/types';
import { SectionTitle } from '@/features/users/shared/components';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

type RestrictionDraft = {
  cleaner_id: number;
  reason: string;
  cleaner?: CleanerRestrictionCleaner | User | null;
};

const MAX_REASON_LENGTH = 500;

const getCleanerName = (
  cleaner?: Pick<User, 'first_name' | 'last_name' | 'email'> | null
) => {
  if (!cleaner) return 'Unknown cleaner';

  const name = [cleaner.first_name, cleaner.last_name]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');

  return name || cleaner.email || 'Unknown cleaner';
};

const toDrafts = (
  restrictions: CleanerRestriction[] = []
): RestrictionDraft[] =>
  restrictions.map((restriction) => ({
    cleaner_id: restriction.cleaner_id,
    reason: restriction.reason ?? '',
    cleaner: restriction.cleaner ?? null,
  }));

const toPayload = (drafts: RestrictionDraft[]): ClientCleanerRestrictionDto[] =>
  drafts.map((draft) => {
    const reason = draft.reason.trim();

    return {
      cleaner_id: draft.cleaner_id,
      ...(reason ? { reason } : {}),
    };
  });

const getComparableRestrictions = (drafts: RestrictionDraft[]) =>
  toPayload(drafts)
    .map((draft) => ({
      cleaner_id: draft.cleaner_id,
      reason: draft.reason ?? '',
    }))
    .sort((a, b) => a.cleaner_id - b.cleaner_id);

export default function ClientCleanerRestrictions() {
  const { userId } = useParams();
  const queryClient = useQueryClient();
  const { canUpdate } = useGetAccess('clients');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const clientId = Number(userId);
  const hasClientId = Boolean(userId) && Number.isFinite(clientId);
  const [drafts, setDrafts] = useState<RestrictionDraft[]>([]);
  const [selectedCleaner, setSelectedCleaner] = useState<User | null>(null);

  const { data: client, isLoading: isClientLoading } = useQuery({
    queryKey: ['clients', userId],
    queryFn: () => getSingleUser(clientId),
    enabled: hasClientId,
    onError: (error) => {
      errorMsg(error, 'Unable to load client cleaner restrictions');
    },
    refetchOnWindowFocus: false,
  });

  const { data: activeProviders, isLoading: isProvidersLoading } = useQuery({
    queryKey: ['providers', 'active'],
    queryFn: getActiveProviders,
    onError: (error) => {
      errorMsg(error, 'Unable to load active cleaners');
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setDrafts(toDrafts(client?.cleaner_restrictions ?? []));
  }, [client?.cleaner_restrictions]);

  const selectedCleanerIds = useMemo(
    () => new Set(drafts.map((draft) => draft.cleaner_id)),
    [drafts]
  );

  const availableCleaners = useMemo(
    () =>
      (activeProviders ?? []).filter(
        (provider) => !selectedCleanerIds.has(provider.id)
      ),
    [activeProviders, selectedCleanerIds]
  );

  const savedComparable = useMemo(
    () => getComparableRestrictions(toDrafts(client?.cleaner_restrictions)),
    [client?.cleaner_restrictions]
  );

  const currentComparable = useMemo(
    () => getComparableRestrictions(drafts),
    [drafts]
  );

  const hasReasonError = drafts.some(
    (draft) => draft.reason.length > MAX_REASON_LENGTH
  );
  const isDirty =
    JSON.stringify(currentComparable) !== JSON.stringify(savedComparable);
  const isLoading = isClientLoading || isProvidersLoading;

  const updateRestrictionsMutation = useMutation({
    mutationFn: () =>
      updateClientCleanerRestrictions(clientId, toPayload(drafts)),
    onSuccess: (result) => {
      queryClient.setQueryData<User | undefined>(
        ['clients', userId],
        (currentClient) =>
          currentClient
            ? {
                ...currentClient,
                cleaner_restrictions: result.cleaner_restrictions,
              }
            : currentClient
      );
      queryClient.invalidateQueries(['clients']);
      queryClient.invalidateQueries(['clients', userId]);
      successMsg('Restricted cleaners updated');
    },
    onError: (error) => {
      errorMsg(error, 'Unable to update restricted cleaners');
    },
  });

  const handleAddCleaner = () => {
    if (!selectedCleaner) return;

    setDrafts((currentDrafts) => [
      ...currentDrafts,
      {
        cleaner_id: selectedCleaner.id,
        reason: '',
        cleaner: selectedCleaner,
      },
    ]);
    setSelectedCleaner(null);
  };

  const handleReasonChange = (cleanerId: number, reason: string) => {
    setDrafts((currentDrafts) =>
      currentDrafts.map((draft) =>
        draft.cleaner_id === cleanerId ? { ...draft, reason } : draft
      )
    );
  };

  const handleRemoveCleaner = (cleanerId: number) => {
    setDrafts((currentDrafts) =>
      currentDrafts.filter((draft) => draft.cleaner_id !== cleanerId)
    );
  };

  const handleSave = () => {
    if (!hasClientId || hasReasonError || !isDirty) return;
    updateRestrictionsMutation.mutate();
  };

  if (!hasClientId) return null;

  if (isLoading) {
    return <SkeletonLoadingTable />;
  }

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <Box sx={{ flex: 1 }}>
            <SectionTitle title="Restricted cleaners" />
          </Box>
          <LoadingButton
            variant="contained"
            loading={updateRestrictionsMutation.isLoading}
            disabled={!canUpdate || !isDirty || hasReasonError}
            onClick={handleSave}
          >
            Save
          </LoadingButton>
        </Stack>

        {canUpdate && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Autocomplete
              fullWidth
              value={selectedCleaner}
              options={availableCleaners}
              onChange={(_event, value) => setSelectedCleaner(value)}
              getOptionLabel={(option) => getCleanerName(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No active cleaners available"
              renderOption={(props, option) => (
                <MenuItem {...props} key={option.id} value={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      src={option.profile_image}
                      alt={getCleanerName(option)}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Box>
                      <Typography variant="body2">
                        {getCleanerName(option)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add cleaner"
                  placeholder=""
                  size="small"
                />
              )}
            />
            <LoadingButton
              variant="outlined"
              disabled={!selectedCleaner}
              onClick={handleAddCleaner}
              sx={{ minWidth: 96 }}
            >
              Add
            </LoadingButton>
          </Stack>
        )}

        {!drafts.length ? (
          <Typography variant="body2" color="text.secondary">
            No restricted cleaners
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={2}>
            {drafts.map((draft) => {
              const cleaner =
                draft.cleaner ??
                activeProviders?.find(
                  (provider) => provider.id === draft.cleaner_id
                ) ??
                null;
              const reasonLength = draft.reason.length;
              const reasonHasError = reasonLength > MAX_REASON_LENGTH;

              return (
                <Stack
                  key={draft.cleaner_id}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'flex-start' }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ minWidth: { md: 260 }, flexShrink: 0 }}
                  >
                    <Avatar
                      src={cleaner?.profile_image ?? undefined}
                      alt={getCleanerName(cleaner)}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {getCleanerName(cleaner)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block' }}
                      >
                        {cleaner?.email ?? `Cleaner #${draft.cleaner_id}`}
                      </Typography>
                    </Box>
                  </Stack>

                  <TextField
                    label="Reason"
                    value={draft.reason}
                    onChange={(event) =>
                      handleReasonChange(draft.cleaner_id, event.target.value)
                    }
                    multiline
                    minRows={2}
                    fullWidth
                    disabled={!canUpdate}
                    error={reasonHasError}
                    helperText={`${reasonLength}/${MAX_REASON_LENGTH}`}
                    inputProps={{ maxLength: MAX_REASON_LENGTH }}
                  />

                  {canUpdate && (
                    <IconButton
                      aria-label={`Remove ${getCleanerName(cleaner)}`}
                      onClick={() => handleRemoveCleaner(draft.cleaner_id)}
                      sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
